import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IonNav } from '@ionic/angular';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CaseService } from 'src/app/services/case.service';
import { CommonService } from 'src/app/services/common.service';
declare var nmi: any;

@Component({
  selector: 'app-nmi',
  templateUrl: './trust-payment.page.html',
  styleUrls: ['./trust-payment.page.scss'],
})
export class TrustPaymentPage implements OnInit {
  @Input() caseId;
  @Input() responseData;
  @Input() debtorDetails;
  title = 'Lateral Payment Portal';
  TrustPaymentForm: FormGroup;
  saveBtnDisabled = false;
  isConfigured = false;
  amount = 0;
  fieldLabels : any = {};
  years: number[] = this.generateYearList();
  months: number[] = this.generateMonthList();
  constructor(
    private formBuilder: FormBuilder,
    private caseService: CaseService,
    private commonService: CommonService,
    private ionNav: IonNav,
    private translate: TranslateService,
    private router: Router
  ) {}

  /**
   * @description
   * This function is called when the component is initialized.
   * It sets the title of the page to the payment type label.
   * It also sets the amount to the net amount of the case.
   * It reads the portal settings from local storage and
   * sets the field labels and checks if the payment method is configured.
   * If the payment method is configured, it initializes the form.
   */
  ngOnInit() {
    this.saveBtnDisabled = false;
    this.amount = this.responseData?.net_amount;
    const portalSettings = JSON.parse(localStorage.getItem('portal_settings'));
    if (this.caseService.isClient('oriel')) {
      portalSettings.fieldLabels = {
        debtor____debtor_1_name: 'Debtor Name',
        debtor____debtor_1_surname: 'Debtor Surname',
      };
    }
    if (portalSettings?.payment_type) {
      const payment_type = portalSettings?.payment_type
        ? portalSettings?.payment_type
        : {};
      if (payment_type?.nmi && payment_type?.nmi?.label) {
        this.title = payment_type.nmi.label;
      }
    }
    if (portalSettings?.fieldLabels) {
      this.fieldLabels = portalSettings.fieldLabels;
    }
    this.isConfigured = true;
    this.initForm();
  }

  /**
   * @description
   * Initializes the form with the debtor's first and last name and the amount.
   * The form fields are:
   * - amount: The amount of the payment.
   * - debtor_first_name: The debtor's first name.
   * - debtor_last_name: The debtor's last name.
   * - card_number: The card number.
   * - expiry_month: The expiry month.
   * - expiry_year: The expiry year.
   * - cvv: The cvv.
   */
  initForm() {
    this.TrustPaymentForm = this.formBuilder.group({
      // The amount of the payment.
      amount: [
        { value: this.amount ? this.amount : 0, disabled: true },
        [Validators.required],
      ],
      // The debtor's first name.
      debtor_first_name: [
        this.debtorDetails?.debtor_1_name
          ? this.debtorDetails?.debtor_1_name
          : '',
        [Validators.required],
      ],
      // The debtor's last name.
      debtor_last_name: [
        this.debtorDetails?.debtor_1_surname
          ? this.debtorDetails?.debtor_1_surname
          : '',
        [Validators.required],
      ],
      // The card number.
      // The pattern is a regular expression that matches the card number format.
      card_number: [
        '',
        [
          Validators.required,
          Validators.pattern('^[0-9]{12,20}$'),
        ],
      ],
      // The expiry month.
      expiry_month: ['', [Validators.required]],
      // The expiry year.
      expiry_year: ['', [Validators.required]],
      // The cvv.
      // The pattern is a regular expression that matches the cvv format.
      cvv: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[0-9]{3}$/),
        ],
      ],
    });
  }

  /**
   * @description
   * Generates a list of years starting from the current year.
   * @returns A list of years.
   */
  generateYearList(): number[] {
    const currentYear = new Date().getFullYear();
    /**
     * Creates an array of 16 elements using the current year as the starting point.
     * The array will contain the years from the current year to 16 years from now.
     * For example, if the current year is 2023, the array will contain the years
     * from 2023 to 2038.
     */
    const years = Array.from({ length: 16 }, (_, index) => currentYear + index);
    return years;
  }

  /**
   * @description
   * Generates a list of months starting from 1 to 12.
   * @returns A list of months.
   */
  generateMonthList(): number[] {
    return Array.from({ length: 12 }, (_, index) => index + 1);
  }

  /**
   * @description
   * Handles the payment process using the Trust Payment Gateway.
   * @remarks
   * The function will show a loader while the payment is being processed.
   * It will also check if the form is valid before sending a request to the server.
   * If the payment is successful, it will show a toast message and navigate to the home page.
   * If the payment fails, it will show a toast message with the error message.
   * @param {void} None.
   * @returns {void}
   */
  proceedPayment(): void {
    this.commonService.showLoader('Payment is processing...');
    if (this.TrustPaymentForm.valid) {
      this.saveBtnDisabled = true;
      const obj = this.TrustPaymentForm.value;
      obj['gateway_id'] = this.responseData.id;
      this.caseService
        .makeTrustPayment(this.caseId, obj)
        .subscribe(
          (res: any) => {
            this.saveBtnDisabled = false;
            this.commonService.dismissLoader();
            if (res.result) {
              // For Oriel client, navigate to payment success page
              if (this.caseService.isClient('oriel')) {
                this.router.navigate(['/payment-success'], {
                  replaceUrl: true,
                  state: {
                    paymentInfo: {
                      referenceNumber: this.caseId,
                      amount: this.amount,
                      transactionId: res.data?.transaction_id || res.data?.id || null
                    },
                    showHeader: true,
                  }
                });
              } else {
                // For other clients, use original behavior
                this.commonService.showToast(res['message'], 'success');
                this.ionNav.popToRoot();
              }
            } else {
              this.commonService.showToast(res['message'], 'danger');
            }
          }
        );
    }
  }

  /**
   * @description
   * Navigates back to the previous page.
   * @returns {void}
   */
  async dismiss(): Promise<void> {
    this.ionNav.pop();
  }
}
